/**
 * Truck fitment data layer -- window.PeragonFitment.
 *
 * Replaces the admin.peragon.com /shopify/makes|models|years|beds|availability
 * endpoints with Shopify data rendered by snippets/truck-fitment-data.liquid:
 *
 *   trucks metaobject                 the truck list (cascade, waitlist, install notes)
 *   variant custom.compatible_trucks  which trucks each cover variant fits
 *   variant custom.ctpdate            Acumatica's capable-to-promise date
 *
 * cover() returns the same shape /availability did, so the renderer in
 * truck-fitment.js is unchanged apart from where the data comes from.
 *
 * Ship-date rules (shipPromise) follow what the admin did in ShipDateBehavior /
 * ShopifyController, with Acumatica's CTP date standing in for its stock + lead-time
 * calculation. All in Central time:
 *   1. start from the CTP date, never earlier than today
 *   2. today, and it is noon or later        -> +1 day
 *   3. Tectonic                               -> +1 day (always; the admin did the same)
 *   4. lands on a weekend                     -> the Monday after
 * Wording: "Ships TODAY if ordered within <countdown>" / "Quickship: Ships Oct 14, 2026"
 * within 3 days / otherwise "Ships: Between Oct 20th and Oct 24th" (4 weekdays later).
 *
 * Truck ids are metaobject handles. They go onto the cart line as _truck_id.
 */
(function () {
  'use strict';

  if (window.PeragonFitment) return;

  const TIMEZONE = 'America/Chicago';
  const CUTOFF_HOUR = 12;
  const QUICKSHIP_DAYS = 3;
  const RANGE_WEEKDAYS = 4;
  const LINE_EXTRA_DAYS = { tectonic: 1 };
  const SECTION_URL = '/?section_id=truck-fitment-data';
  const DAY = 24 * 60 * 60 * 1000;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ---------------------------------------------------------------------------
  // Dates. A calendar day is a UTC-midnight timestamp, so adding DAY never trips
  // over a DST change and the shopper's own timezone never enters into it.
  // ---------------------------------------------------------------------------

  function parseDay(value) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null;
  }

  //Today's date and seconds since midnight, in Central time.
  function centralClock(now) {
    const parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(new Date(now)).forEach(p => { parts[p.type] = Number(p.value); });
    return {
      day: Date.UTC(parts.year, parts.month - 1, parts.day),
      seconds: parts.hour * 3600 + parts.minute * 60 + parts.second
    };
  }

  function weekday(day) {
    return new Date(day).getUTCDay();
  }

  function rollPastWeekend(day) {
    const w = weekday(day);
    if (w === 6) return day + 2 * DAY;
    if (w === 0) return day + DAY;
    return day;
  }

  function addWeekdays(day, count) {
    while (count > 0) {
      day += DAY;
      if (weekday(day) !== 0 && weekday(day) !== 6) count--;
    }
    return day;
  }

  function isoDay(day) {
    return new Date(day).toISOString().slice(0, 10);
  }

  //"Oct 04, 2026" -- PHP 'M d, Y', what the admin printed.
  function formatLong(day) {
    const d = new Date(day);
    return MONTHS[d.getUTCMonth()] + ' ' + String(d.getUTCDate()).padStart(2, '0') + ', ' + d.getUTCFullYear();
  }

  //"Oct 4th" -- PHP 'M jS'.
  function formatOrdinal(day) {
    const n = new Date(day).getUTCDate();
    const suffix = (n % 100 >= 11 && n % 100 <= 13) ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] || 'th');
    return MONTHS[new Date(day).getUTCMonth()] + ' ' + n + suffix;
  }

  const QUICKSHIP_LABEL = '<span class="quickship"><i class="fas fa-shipping-fast"></i> Quickship:</span>';

  /**
   * The ship promise for one variant.
   *
   * @param {String} ctp  custom.ctpdate, YYYY-MM-DD
   * @param {String} line 'landmark' | 'tectonic'
   * @param {Number} now  ms timestamp, for testing; defaults to now
   * @return {Object|null} { quoted, quickship, html } -- null with no CTP date
   */
  function shipPromise(ctp, line, now) {
    const ctpDay = parseDay(ctp);
    if (ctpDay === null) return null;

    const clock = centralClock(now === undefined ? Date.now() : now);
    let day = Math.max(ctpDay, clock.day);
    if (day === clock.day && clock.seconds >= CUTOFF_HOUR * 3600) day += DAY;
    day += (LINE_EXTRA_DAYS[line] || 0) * DAY;
    day = rollPastWeekend(day);

    let html;
    let quickship = true;
    if (day === clock.day) {
      //Only reachable before the cutoff, so the countdown is always positive.
      const seconds = CUTOFF_HOUR * 3600 - clock.seconds;
      html = QUICKSHIP_LABEL + ' Ships TODAY if ordered within ' +
        '<span class="quickship" data-role="countdown" data-spent="0" data-seconds="' + seconds + '"></span>';
    } else if (day - clock.day <= QUICKSHIP_DAYS * DAY) {
      html = QUICKSHIP_LABEL + ' Ships ' + formatLong(day);
    } else {
      quickship = false;
      html = 'Ships: Between ' + formatOrdinal(day) + ' and ' + formatOrdinal(addWeekdays(day, RANGE_WEEKDAYS));
    }

    return { quoted: isoDay(day), quickship: quickship, html: html };
  }

  // ---------------------------------------------------------------------------
  // Parts and accessories. They have no CTP date, so they are quoted by the
  // lead-time tiers ported from the admin (ShopifySyncService::partLeadDays /
  // addBusinessDays) -- KEEP THE TWO IN STEP. Only used when the cart has no
  // cover; with a cover, parts take the cover's date instead.
  // ---------------------------------------------------------------------------

  //Business days for one part, from its title: panel assembly 10, rail assembly
  //7, anything else 3. The bare word "rail"/"panel" is not enough -- it must be
  //an assembly ("assembly" or "assy").
  function partLeadDays(title) {
    const t = String(title || '').toLowerCase();
    const assembly = t.indexOf('assembly') !== -1 || t.indexOf('assy') !== -1;
    if (assembly && /\bpanel\b/.test(t)) return 10;
    if (assembly && /\brail\b/.test(t)) return 7;
    return 3;
  }

  /**
   * The ship promise for the parts in a cart with no cover. They ship as one box,
   * so every line gets the longest tier, counted in weekdays starting tomorrow
   * (Central time).
   *
   * @param {Array}  titles Cart line titles (/cart.js item.title)
   * @param {Number} now    ms timestamp, for testing; defaults to now
   * @return {Object} { quoted, quickship, html } -- same shape as shipPromise()
   */
  function partPromise(titles, now) {
    const clock = centralClock(now === undefined ? Date.now() : now);
    const days = titles.map(partLeadDays).reduce((a, b) => Math.max(a, b), 3);
    const day = addWeekdays(clock.day, days);
    return { quoted: isoDay(day), quickship: false, html: 'Ships ' + formatLong(day) };
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  function parseBlob(scope) {
    const el = scope.querySelector('script[data-truck-fitment-data]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[truck-fitment-data] unreadable fitment data', err);
      return null;
    }
  }

  async function fetchSection() {
    const res = await fetch(SECTION_URL, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    return parseBlob(new DOMParser().parseFromString(await res.text(), 'text/html'));
  }

  //"1500" on a Silverado 1500 repeats the model; only a trim that adds something
  //(e.g. "Lightning") is shown.
  function extraTrim(truck) {
    const trim = String(truck.trim || '').trim();
    return trim && truck.model.toLowerCase().indexOf(trim.toLowerCase()) === -1 ? trim : '';
  }

  function bedLabel(truck) {
    const trim = extraTrim(truck);
    return (trim ? trim + ' - ' : '') + truck.bed;
  }

  function build(raw) {
    const trucks = (raw.trucks || [])
      .filter(t => t && t.id && t.active !== false && t.make && t.model)
      .map(t => Object.assign({}, t, {
        id: String(t.id),
        make: String(t.make).trim(),
        model: String(t.model).trim(),
        start: Number(t.start) || 0,
        end: Number(t.end) || Number(t.start) || 0,
        inches: Number(t.inches) || 0,
        notes: t.notes || {}
      }));

    const byId = new Map(trucks.map(t => [t.id, t]));
    const lines = {};
    //truck id -> Set of lines with at least one variant for it
    const fittedLines = new Map();

    Object.entries(raw.lines || {}).forEach(([line, data]) => {
      const variants = ((data && data.variants) || []).map(v => Object.assign({}, v, {
        id: String(v.id),
        trucks: (v.trucks || []).map(String)
      }));
      lines[line] = { title: (data && data.title) || '', variants: variants };
      variants.forEach(v => v.trucks.forEach(id => {
        if (!fittedLines.has(id)) fittedLines.set(id, new Set());
        fittedLines.get(id).add(line);
      }));
    });

    return { trucks: trucks, byId: byId, lines: lines, fittedLines: fittedLines };
  }

  let loading = null;

  /**
   * Resolves to the data model. The inline copy is used when it is complete; the
   * section fetch only happens on a paginated URL, or a page without the snippet.
   */
  function ready() {
    if (!loading) {
      loading = Promise.resolve(parseBlob(document))
        .then(raw => (raw && raw.complete ? raw : fetchSection()))
        .then(raw => build(raw || {}))
        .catch(err => {
          console.error('[truck-fitment-data] could not load fitment data', err);
          return build({});
        });
    }
    return loading;
  }

  // ---------------------------------------------------------------------------
  // Cascade -- each returns [value, label] pairs in display order
  // ---------------------------------------------------------------------------

  function distinct(values) {
    return values.filter((v, i, all) => all.indexOf(v) === i);
  }

  function makes(model) {
    return distinct(model.trucks.map(t => t.make))
      .sort((a, b) => a.localeCompare(b))
      .map(m => [m, m]);
  }

  function models(model, make) {
    return distinct(model.trucks.filter(t => t.make === make).map(t => t.model))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map(m => [m, m]);
  }

  function years(model, make, modelName) {
    const all = [];
    model.trucks.forEach(t => {
      if (t.make !== make || t.model !== modelName) return;
      for (let y = t.start; y && y <= t.end; y++) all.push(y);
    });
    return distinct(all).sort((a, b) => b - a).map(y => [String(y), String(y)]);
  }

  /**
   * Bed lengths for one make/model/year, keyed by truck id.
   *
   * Overlapping entries can describe the same bed for one year (2005-2027 and
   * 2015-2027 F-150 66"), which would list "66"" twice with only one of them fitting
   * anything. Until the data is cleaned up, the first entry that has a cover linked
   * wins, then the first in metaobject order. The choice ignores the page's own line,
   * so a saved truck id means the same bed on every page.
   */
  function beds(model, make, modelName, year) {
    const y = Number(year);
    const matches = model.trucks.filter(t =>
      t.make === make && t.model === modelName && t.start <= y && y <= t.end);

    const linked = t => model.fittedLines.has(t.id) ? 0 : 1;
    const chosen = new Map();
    matches
      .map((t, index) => ({ t: t, index: index }))
      .sort((a, b) => linked(a.t) - linked(b.t) || a.index - b.index)
      .forEach(({ t }) => {
        const label = bedLabel(t);
        if (!chosen.has(label.toLowerCase())) chosen.set(label.toLowerCase(), t);
      });

    return Array.from(chosen.values())
      .sort((a, b) => a.inches - b.inches || bedLabel(a).localeCompare(bedLabel(b)))
      .map(t => [t.id, bedLabel(t)]);
  }

  // ---------------------------------------------------------------------------
  // Covers
  // ---------------------------------------------------------------------------

  function lineKey(category) {
    return String(category || '').indexOf('tectonic') !== -1 ? 'tectonic' : 'landmark';
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  //Install notes are multi-line text: escape, keep the line breaks.
  function notesHtml(text) {
    const value = String(text || '').trim();
    return value ? value.replace(/\r?\n/g, '<br>') : '';
  }

  //"LimitedHDx Edition" -> "LimitedHDx"; "Max" -> "Max".
  function editionName(variant) {
    return String(variant.edition || '').replace(/\s+edition$/i, '').trim();
  }

  function truckTitle(truck, year) {
    const years = year || (truck.start === truck.end ? truck.start : truck.start + '-' + truck.end);
    return [years, truck.make, truck.model, extraTrim(truck)].filter(Boolean).join(' ');
  }

  function permalink(variantId, properties) {
    const params = new URLSearchParams({ id: variantId, quantity: '1' });
    Object.keys(properties).forEach(key => params.set('properties[' + key + ']', properties[key]));
    return '/cart/add?' + params.toString();
  }

  /**
   * Covers for one truck on one line, in the shape /shopify/availability returned.
   *
   * @param {Object} model    From ready()
   * @param {String} truckId  Trucks metaobject handle
   * @param {String} category Page reporting category ('landmark', 'tectonic', 'tectonic-2')
   * @param {String} year     The year picked, for the truck's title. A truck entry
   *                          covers a range, so its own data cannot say which year.
   */
  function cover(model, truckId, category, year) {
    const truck = model.byId.get(String(truckId || ''));
    if (!truck) return { has_error: true, has_products: false, products: {} };

    const line = lineKey(category);
    const lineData = model.lines[line] || { title: '', variants: [] };
    const short = truckTitle(truck, year);
    const configured = short + ' with ' + truck.bed + ' Bed';
    const notes = notesHtml(truck.notes[line]);

    const products = {};
    lineData.variants.forEach(v => {
      if (v.available === false || v.trucks.indexOf(truck.id) === -1) return;

      const edition = editionName(v);
      const promise = shipPromise(v.ctp, line);
      if (!promise) console.warn('[truck-fitment-data] variant ' + v.id + ' (' + v.sku + ') has no custom.ctpdate');

      const properties = { _truck_id: truck.id, 'Configured Truck': configured };
      if (promise) {
        properties._quoted = promise.quoted;
        properties._quickship = promise.quickship ? '1' : '0';
      }
      if (notes) properties['Fitment Notes'] = notes;
      if (line === 'tectonic') properties.Edition = 'Tectonic ' + edition;

      products[v.id] = {
        short_name: edition,
        //Landmark headings show " - <Edition>"; Tectonic's never have.
        title: line === 'tectonic' ? lineData.title : lineData.title + ' - ' + v.edition,
        sku: v.sku,
        price: v.price / 100,
        variant_id: v.id,
        'Estimated Ship Date': promise ? promise.html : '',
        quoted: promise ? promise.quoted : '',
        quickship: promise && promise.quickship ? 1 : 0,
        _quickship: promise && promise.quickship ? 1 : 0,
        'Fitment Notes': notes || null,
        add_to_cart: permalink(v.id, properties),
        cart_add: { items: [{ id: v.id, quantity: 1, properties: properties, selling_plan: null }] }
      };
    });

    const hasProducts = Object.keys(products).length > 0;
    return {
      has_error: false,
      has_products: hasProducts,
      has_waitlist: truck.waitlist !== false,
      truck_id: truck.id,
      configured_truck: configured,
      configured_truck_short: short,
      configured_truck_notes: notes || null,
      products: products
    };
  }

  // Fitment Notes from Cake
  const CAKE_NOTES_URL = 'https://admin.peragon.com/shopify/fitment-notes';
  const CAKE_TIMEOUT_MS = 2500;
  const cakeNotes = new Map();

  function fetchCakeNotes(truck, year) {
    const key = truck.id + '|' + year;
    if (!cakeNotes.has(key)) {
      const params = new URLSearchParams({
        truck_id: truck.id, make: truck.make, model: truck.model, year: year,
        bed: truck.bed, inches: truck.inches, trim: truck.trim || ''
      });
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), CAKE_TIMEOUT_MS);
      cakeNotes.set(key, fetch(CAKE_NOTES_URL + '?' + params, { signal: abort.signal })
        .then(res => res.ok ?res.json() : null)
        .then(json => {
          if(json && !json.found) console.warn('Cake has no truck for ' + key, params.toString());
          return json && json.found ? json : null;
        })
        .catch(err => { console.warn('[truck-fitment-data] Failed to fetch Cake notes for ' + key, err); })
        .finally(() => clearTimeout(timer)));
    }
    return cakeNotes.get(key);
  }

  async function withCakeNotes(model, cover, truckId, category, year) {
    if (!cover || !cover.has_products || cover.configured_truck_notes) return cover;

    const truck = model.byId.get(String(truckId || ''));
    const line = lineKey(category);
    if(!truck || String(truck.notes[line] || '').trim()) return cover;

    const y = Math.min(Math.max(Number(year) || truck.end, truck.start), truck.end);
    const notes = await fetchCakeNotes(truck, y);
    const html = notes && notes[line];
    if (!html) return cover;

    cover.configured_truck_notes = html;
    Object.values(cover.products).forEach(p => {
      const properties = p.cart_add.items[0].properties;
      properties['Fitment Notes'] = html;
      p['Fitment Notes'] = html;
      p.add_to_cart = permalink(p.variant_id, properties);
    });

    return cover;
  }

  //Which lines have a cover for this truck -- what the collection cards need.
  function fits(model, truckId) {
    const lines = model.fittedLines.get(String(truckId || ''));
    return {
      known: model.byId.has(String(truckId || '')),
      landmark: !!(lines && lines.has('landmark')),
      tectonic: !!(lines && lines.has('tectonic'))
    };
  }

  window.PeragonFitment = {
    ready: ready,
    makes: makes,
    models: models,
    years: years,
    beds: beds,
    cover: cover,
    withCakeNotes: withCakeNotes,
    fits: fits,
    lineKey: lineKey,
    shipPromise: shipPromise,
    partPromise: partPromise
  };
})();
